import { z } from "zod";
import { Grenade, Scope, Side } from "./stuff.ts";

/**
 * Strat boards: free planning on a map, by phase (Setup, Fake B, Hit A). Every
 * drawing sits in radar fractions (0 to 1 across the radar image), so it stays
 * put whatever the screen size; a grenade can point at a saved single.
 */

export const STRAT_COLORS = ["white", "yellow", "orange", "red", "blue", "green"] as const;
export const StratColor = z.enum(STRAT_COLORS);
export type StratColor = z.infer<typeof StratColor>;

/** How thick arrows and strokes are drawn. */
export const STRAT_WIDTHS = ["thin", "normal", "bold"] as const;
export const StratWidth = z.enum(STRAT_WIDTHS);
export type StratWidth = z.infer<typeof StratWidth>;

export const MAX_STRAT_PHASES = 8;
export const MAX_STRAT_ITEMS = 300;
export const MAX_PEN_POINTS = 400;

/** A spot on the radar, in fractions of its size; a little outside is allowed for arrows that run off the edge. */
const Frac = z.number().finite().min(-0.25).max(1.25);
const Point = z.tuple([Frac, Frac]);
const ItemId = z.string().min(1).max(24);
const Level = z.enum(["upper", "lower"]);

const base = { id: ItemId, level: Level.default("upper") };

export const StratToken = z.object({
  ...base,
  kind: z.literal("token"),
  side: Side,
  /** 1 to 5: which player. */
  n: z.number().int().min(1).max(5),
  u: Frac,
  v: Frac,
  /** Where the player looks: the token points at it. */
  look: Point.nullable().default(null),
  /** Whether the line to where they look is drawn. */
  view: z.boolean().default(true),
});

export const StratArrow = z.object({ ...base, kind: z.literal("arrow"), color: StratColor, width: StratWidth.default("normal"), from: Point, to: Point });

export const StratPen = z.object({ ...base, kind: z.literal("pen"), color: StratColor, width: StratWidth.default("normal"), points: z.array(Point).min(2).max(MAX_PEN_POINTS) });

export const StratText = z.object({ ...base, kind: z.literal("text"), color: StratColor, u: Frac, v: Frac, text: z.string().trim().min(1).max(80) });

export const StratGrenade = z.object({
  ...base,
  kind: z.literal("grenade"),
  grenade: Grenade,
  u: Frac,
  v: Frac,
  /** The saved single whose landing this grenade sits on: the board links to it. */
  stuffId: z.string().uuid().nullable().default(null),
});

export const StratItem = z.discriminatedUnion("kind", [StratToken, StratArrow, StratPen, StratText, StratGrenade]);
export type StratItem = z.infer<typeof StratItem>;
export type StratItemKind = StratItem["kind"];

export const StratPhase = z.object({
  id: ItemId,
  name: z.string().trim().min(1, "Name the phase").max(30),
  items: z.array(StratItem).max(MAX_STRAT_ITEMS, `A phase holds at most ${MAX_STRAT_ITEMS} drawings`),
});
export type StratPhase = z.infer<typeof StratPhase>;

export const StratInput = z.object({
  map: z.string(),
  side: Side,
  title: z.string().trim().min(2, "At least 2 characters").max(60, "At most 60 characters"),
  notes: z.string().trim().max(1000).default(""),
  phases: z
    .array(StratPhase)
    .min(1)
    .max(MAX_STRAT_PHASES, `At most ${MAX_STRAT_PHASES} phases`)
    .refine((ps) => new Set(ps.map((p) => p.id)).size === ps.length, { message: "Two phases have the same id" }),
});
export type StratInput = z.infer<typeof StratInput>;

export const CreateStratInput = z.intersection(StratInput, z.object({ scope: Scope }));
export type CreateStratInput = z.infer<typeof CreateStratInput>;

/** A save from the board: what it was based on, so two people drawing at once do not erase each other. */
export const UpdateStratInput = z.intersection(StratInput, z.object({ baseUpdatedAt: z.string() }));
export type UpdateStratInput = z.infer<typeof UpdateStratInput>;

export interface Strat extends StratInput {
  id: string;
  teamId: string | null;
  author: { id: string; name: string };
  /** Who saved it last. */
  editor: { id: string; name: string } | null;
  canEdit: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A board's list entry: no drawings. */
export type StratSummary = Omit<Strat, "phases"> & { phaseNames: string[]; items: number };

/** A new board: one empty Setup phase. */
export const emptyPhases = (): StratPhase[] => [{ id: "p1", name: "Setup", items: [] }];

/** How far (radar fractions) a dropped grenade snaps to a saved landing. */
export const STRAT_SNAP = 0.025;
